import type {
  User,
  TradingPair,
  TradingConfig,
  Trade,
  PortfolioData,
  Suggestion,
  ConfigFormData,
  TelegramAuthData,
  AggregatedChartData,
  ChartInterval,
  ChartRange,
  WithdrawResponse,
  WithdrawTransaction,
} from '@/types';

const API_BASE = import.meta.env.VITE_API_URL || '';

// Response types matching backend
interface WalletVerifyResponse {
  success: boolean;
  isNewUser: boolean;
  sessionId?: string;
  user?: BackendUser;
  registrationToken?: string;
  walletAddress?: string;
}

interface TelegramVerifyResponse {
  success: boolean;
  isNewUser: boolean;
  sessionId?: string;
  user?: BackendUser;
  registrationToken?: string;
  telegramUserId?: number;
  telegramUsername?: string;
}

interface RegisterResponse {
  success: boolean;
  sessionId: string;
  userId: number;
  isNewUser?: boolean;
  scwAddress?: string;
  message?: string;
}

interface BackendUser {
  userId: number;
  wallet?: string;
  scwAddress?: string;
  telegram?: {
    userId: number;
    chatId: string;
    username: string | null;
  } | null;
  hasTelegram?: boolean;
  hasWallet?: boolean;
  telegramUsername?: string;
  username?: string;
  registeredAt?: string;
  registeredVia?: string;
  isActive?: boolean;
  configCount?: number;
}

// Transform backend user to frontend User type
function transformUser(backendUser: BackendUser): User {
  return {
    userId: backendUser.userId,
    walletAddress: backendUser.wallet || null,
    scwAddress: backendUser.scwAddress || null,
    telegramUserId: backendUser.telegram?.userId?.toString() || null,
    telegramUsername: backendUser.telegram?.username || backendUser.telegramUsername || null,
    registeredAt: backendUser.registeredAt || new Date().toISOString(),
    registeredVia: (backendUser.registeredVia as User['registeredVia']) || 'wallet',
  };
}

class ApiClient {
  private token: string | null = null;

  setToken(token: string | null) {
    this.token = token;
  }

  private async request<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<T> {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      ...(options.headers as Record<string, string>),
    };

    if (this.token) {
      headers['Authorization'] = `Bearer ${this.token}`;
    }

    console.log(`[API] ${options.method || 'GET'} ${endpoint}`, this.token ? '(authenticated)' : '(no token)');

    const response = await fetch(`${API_BASE}${endpoint}`, {
      ...options,
      headers,
    });

    console.log(`[API] Response ${endpoint}: ${response.status} ${response.statusText}`);

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Request failed' }));
      console.error(`[API] Error ${endpoint}:`, error);
      throw new Error(error.error || error.message || `HTTP ${response.status}`);
    }

    const data = await response.json();
    console.log(`[API] Success ${endpoint}:`, data);
    return data;
  }

  // Auth endpoints
  async getWalletNonce(address: string): Promise<{ nonce: string }> {
    return this.request(`/api/auth/wallet/nonce?address=${address}`);
  }

  async verifyWalletSignature(
    address: string,
    message: string,
    signature: string
  ): Promise<{
    authenticated: boolean;
    isNewUser: boolean;
    sessionToken?: string;
    user?: User;
    registrationToken?: string;
    existingWallet?: string;
  }> {
    const response = await this.request<WalletVerifyResponse>('/api/auth/wallet/verify', {
      method: 'POST',
      body: JSON.stringify({ address, message, signature }),
    });

    return {
      authenticated: response.success && !response.isNewUser,
      isNewUser: response.isNewUser,
      sessionToken: response.sessionId,
      user: response.user ? transformUser(response.user) : undefined,
      registrationToken: response.registrationToken,
      existingWallet: response.walletAddress,
    };
  }

  async verifyTelegramAuth(data: TelegramAuthData): Promise<{
    authenticated: boolean;
    isNewUser: boolean;
    sessionToken?: string;
    user?: User;
    registrationToken?: string;
    conflict?: boolean;
    existingUsername?: string;
  }> {
    const response = await this.request<TelegramVerifyResponse>('/api/auth/telegram/verify', {
      method: 'POST',
      body: JSON.stringify(data),
    });

    return {
      authenticated: response.success && !response.isNewUser,
      isNewUser: response.isNewUser,
      sessionToken: response.sessionId,
      user: response.user ? transformUser(response.user) : undefined,
      registrationToken: response.registrationToken,
    };
  }

  async completeRegistration(
    registrationToken: string,
    walletAddress?: string
  ): Promise<{
    success: boolean;
    sessionToken: string;
    user: User;
  }> {
    const response = await this.request<RegisterResponse>('/api/auth/register', {
      method: 'POST',
      body: JSON.stringify({ registrationToken, walletAddress, agreeLegal: true }),
    });

    // Fetch full user data after registration
    this.setToken(response.sessionId);
    const meResponse = await this.request<BackendUser>('/api/me');

    return {
      success: response.success,
      sessionToken: response.sessionId,
      user: transformUser(meResponse),
    };
  }

  async logout(): Promise<{ success: boolean }> {
    return this.request('/api/auth/logout', { method: 'POST' });
  }

  // User endpoints
  async getCurrentUser(): Promise<{ user: User }> {
    const response = await this.request<BackendUser>('/api/me');
    return { user: transformUser(response) };
  }

  async linkTelegram(telegramData: TelegramAuthData): Promise<{
    success: boolean;
    user: User;
  }> {
    const response = await this.request<{ success: boolean }>('/api/me/link-telegram', {
      method: 'POST',
      body: JSON.stringify(telegramData),
    });

    // Fetch updated user
    const meResponse = await this.request<BackendUser>('/api/me');

    return {
      success: response.success,
      user: transformUser(meResponse),
    };
  }

  // Trading pairs
  async getTradingPairs(): Promise<{ pairs: TradingPair[] }> {
    const response = await this.request<{ pairs: BackendPair[] }>('/api/pairs');
    return {
      pairs: response.pairs.map(transformPair),
    };
  }

  // Configs
  async getConfigs(): Promise<{ configs: TradingConfig[] }> {
    const response = await this.request<{ configs: BackendConfig[] }>('/api/configs');
    return {
      configs: response.configs.map(transformConfig),
    };
  }

  async getConfig(configId: number): Promise<{ config: TradingConfig }> {
    const response = await this.request<{ config: BackendConfig }>(`/api/configs/${configId}`);
    return { config: transformConfig(response.config) };
  }

  async createConfig(data: ConfigFormData): Promise<{
    success: boolean;
    configId: number;
  }> {
    return this.request('/api/configs', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateConfig(
    configId: number,
    data: Partial<ConfigFormData>
  ): Promise<{ success: boolean }> {
    return this.request(`/api/configs/${configId}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async deleteConfig(configId: number): Promise<{ success: boolean }> {
    return this.request(`/api/configs/${configId}`, {
      method: 'DELETE',
    });
  }

  // Portfolio & Trades
  async getPortfolio(): Promise<PortfolioData> {
    const response = await this.request<BackendPortfolio>('/api/portfolio');
    return transformPortfolio(response);
  }

  async getTrades(limit = 50, offset = 0): Promise<{ trades: Trade[] }> {
    const response = await this.request<{ trades: BackendTrade[] }>(`/api/trades?limit=${limit}&offset=${offset}`);
    return {
      trades: response.trades.map(transformTrade),
    };
  }

  async getChartData(
    interval: ChartInterval = '1d',
    range: ChartRange = 'month'
  ): Promise<AggregatedChartData> {
    return this.request<AggregatedChartData>(
      `/api/chart-data?interval=${interval}&range=${range}`
    );
  }

  // Suggestions
  async getSuggestion(): Promise<{ suggestion: Suggestion }> {
    // Request new suggestion via POST
    const response = await this.request<{ success: boolean; suggestion: string }>('/api/suggestion', {
      method: 'POST',
    });

    return {
      suggestion: {
        suggestionId: Date.now(),
        suggestion: response.suggestion,
        createdAt: new Date().toISOString(),
      },
    };
  }

  // Withdraw
  async initiateWithdraw(chainId: number): Promise<WithdrawResponse> {
    return this.request<WithdrawResponse>('/api/withdraw', {
      method: 'POST',
      body: JSON.stringify({ chainId }),
    });
  }

  async getWithdrawStatus(): Promise<{ withdrawals: WithdrawTransaction[] }> {
    return this.request('/api/withdrawals');
  }
}

// Backend response types
interface BackendPair {
  PairID: number;
  PairName: string;
  ChainID: number;
  ChainName: string;
  BaseSymbol: string;
  QuoteSymbol: string;
  DEXType: string;
  IsActive: number;
}

interface BackendConfig {
  ConfigID: number;
  PairID: number;
  PairName: string;
  ChainName: string;
  BaseSymbol: string;
  QuoteSymbol: string;
  TradePercentage: number;
  TriggerPercentage: number;
  MinimumAmount: number;
  MaxAmount: number;
  Multiplier: number;
  IsActive: number;
  CreatedAt: string;
}

interface BackendTrade {
  TradeID: number;
  PairID: number;
  Action: 'BUY' | 'SELL';
  QuantitySent: number;
  QuantityReceived: number;
  Price: number;
  TxHash: string;
  CreatedAt: string;
  PairName: string;
  ChainName: string;
  BaseSymbol: string;
  QuoteSymbol: string;
  ExplorerURL: string | null;
}

interface BackendPortfolio {
  trades: BackendTrade[];
  currentBalances: Array<{
    TokenID: number;
    Symbol: string;
    TokenAddress: string;
    ChainID: number;
    ChainName: string;
    Balance: number;
    PriceUSDC?: number;
    BalanceUSDC?: number;
    LastUpdated?: string;
  }>;
  stats: {
    totalTrades: number;
    buyCount: number;
    sellCount: number;
    estimatedPnL: number;
    totalDepositedValue: number;
    totalWithdrawnValue: number;
  };
  chartData?: {
    portfolioTimeline: Array<{
      timestamp: string;
      balance: number;
    }>;
  };
}

// Transform functions
function transformPair(p: BackendPair): TradingPair {
  return {
    pairId: p.PairID,
    pairName: p.PairName,
    chainId: p.ChainID,
    chainName: p.ChainName,
    baseSymbol: p.BaseSymbol,
    quoteSymbol: p.QuoteSymbol,
    dexType: p.DEXType,
    isActive: p.IsActive === 1,
  };
}

function transformConfig(c: BackendConfig): TradingConfig {
  return {
    configId: c.ConfigID,
    pairId: c.PairID,
    pairName: c.PairName,
    chainName: c.ChainName,
    baseSymbol: c.BaseSymbol,
    quoteSymbol: c.QuoteSymbol,
    tradePercentage: c.TradePercentage,
    triggerPercentage: c.TriggerPercentage,
    minimumAmount: c.MinimumAmount,
    maxAmount: c.MaxAmount,
    multiplier: c.Multiplier,
    isActive: c.IsActive === 1,
    createdAt: c.CreatedAt,
  };
}

function transformTrade(t: BackendTrade): Trade {
  return {
    tradeId: t.TradeID,
    pairId: t.PairID,
    action: t.Action,
    quantitySent: t.QuantitySent,
    quantityReceived: t.QuantityReceived,
    price: t.Price,
    txHash: t.TxHash,
    createdAt: t.CreatedAt,
    pairName: t.PairName,
    chainName: t.ChainName,
    baseSymbol: t.BaseSymbol,
    quoteSymbol: t.QuoteSymbol,
    explorerUrl: t.ExplorerURL,
  };
}

function transformPortfolio(p: BackendPortfolio): PortfolioData {
  // Calculate total value using BalanceUSDC if available, fallback to Balance
  const totalValue = p.currentBalances.reduce((sum, b) => sum + (b.BalanceUSDC ?? b.Balance ?? 0), 0);
  const pnl = p.stats?.estimatedPnL || 0;
  const deposited = p.stats?.totalDepositedValue || 1;

  return {
    totalValue,
    totalPnL: pnl,
    totalPnLPercent: deposited > 0 ? (pnl / deposited) * 100 : 0,
    balances: p.currentBalances.map((b) => ({
      tokenId: b.TokenID,
      symbol: b.Symbol,
      tokenAddress: b.TokenAddress,
      chainId: b.ChainID,
      chainName: b.ChainName,
      balance: b.Balance,
      usdValue: b.BalanceUSDC ?? b.Balance ?? 0,
      lastUpdated: b.LastUpdated || new Date().toISOString(),
    })),
    chartData: p.chartData?.portfolioTimeline?.map((point) => ({
      timestamp: point.timestamp,
      value: point.balance,
      pnl: 0,
    })) || [],
  };
}

export const api = new ApiClient();
